import SimplePeer from "simple-peer";
import { TypedEventEmitter } from "../utils";
import { timeoutPromise } from "../utils";
import { toast } from "sonner";
import * as Sentry from "@sentry/react";
import { JoinRequest, SignalingProvider } from "./signaling/SignalingProvider";

enum ConnectionState {
	Disconnected = "disconnected",
	Initializing = "initializing",
	Connecting = "connecting",
	Ready = "ready",
	Destroying = "destroying",
}

/**
 * Manages WebRTC peer connections for multiplayer functionality
 * Handles host and client connection flows through the selected signaling provider
 */
export class ConnectionManager extends TypedEventEmitter<{
	stateChange: ConnectionState
	ready: void
	onMessage: unknown
}> {
	private readonly peers = new Map<string, SimplePeer.Instance>();
	private readonly cleanup: (() => Promise<void>)[] = [];
	private _roomId: string = null!;
	private signaling: SignalingProvider;

	public get roomId() {
		return this._roomId;
	}

	private set roomId(roomId: string) {
		this._roomId = roomId;
	}

	private _state: ConnectionState = ConnectionState.Disconnected;

	public get state() {
		return this._state;
	}

	private set state(state: ConnectionState) {
		if (this._state === state) return;
		
		this._state = state;
		this.emit('stateChange', this._state);

		if (this._state === ConnectionState.Ready) {
			this.emit('ready', undefined);
		}
	}

	static async host(signalingProvider: SignalingProvider) {
		const conn = new ConnectionManager(true, signalingProvider);
		await conn.initHost();
		return conn;
	}

	static async join(roomId: string, signalingProvider: SignalingProvider) {
		const conn = new ConnectionManager(false, signalingProvider);
		conn.roomId = roomId;
		
		try {
			await conn.requestToJoinRoom();
			
			// Wait for connection to be ready
			await timeoutPromise(
				new Promise<void>((resolve) => {
					conn.once('ready', () => resolve());
				}),
				15000 // timeout request after 15 seconds
			);
			
			return conn;
		} catch (error) {
			// Clean up resources on error
			await conn.cleanupResources();
			throw error;
		}
	}

	constructor(public readonly isHost: boolean = false, signalingProvider: SignalingProvider) {
		super();
		this.signaling = signalingProvider;
	}

	/**
	 * Sends data to all connected peers
	 * If you are the host, this will send the data to all peers. 
	 * If you are a client, this will send the data to the host since 
	 * they are your only peer.
	 */
	send(data: unknown) {
		const serializedData = JSON.stringify(data);
		
		for (const peer of this.peers.values()) {
			if (!peer.connected) continue;
			peer.send(serializedData);
		}
	}

	/**
	 * Send data to a specific peer by ID
	 * This will only work if you are the host.
	 */
	sendTo(peerId: string, data: unknown) {
		if (!this.isHost) {
			throw new Error('Only the host can send to a specific peer');
		}

		const peer = this.peers.get(peerId);
		if (!peer) {
			throw new Error(`Peer ${peerId} not found`);
		}

		if (!peer.connected) {
			throw new Error(`Peer ${peerId} not connected`);
		}

		peer.send(JSON.stringify(data));
	}

	/**
	 * Clean up all resources used by this connection manager
	 */
	async cleanupResources() {
		// Run all cleanup functions
		for (const cleanup of this.cleanup) {
			try {
				await cleanup();
			} catch (e) {
				console.error("Error during cleanup:", e);
			}
		}
		this.cleanup.length = 0;
		
		// Destroy all peers
		for (const peer of this.peers.values()) {
			peer.destroy();
		}
		this.peers.clear();
		
		this.state = ConnectionState.Disconnected;
	}

	/**
	 * Initialize this connection as a host
	 */
	private async initHost() {
		this.state = ConnectionState.Initializing;

		try {
			// Clean up previous rooms
			await this.signaling.cleanupPreviousRooms();
			
			// Create a new room
			this.roomId = await this.signaling.createRoom();

			// Set up event handlers for the signaling provider
			this.signaling.on('joinRequest', (request) => this.handleJoinRequest(request));
			this.signaling.on('answerUpdate', (request) => this.handleAnswer(request));
			
			// Subscribe to join requests
			const unsub = await this.signaling.subscribeToJoinRequests(this.roomId);
			this.cleanup.push(unsub);
			
			this.state = ConnectionState.Ready;
		} catch (error) {
			await this.cleanupResources();
			throw error;
		}
	}

	/**
	 * Request to join an existing room
	 */
	private async requestToJoinRoom() {
		this.state = ConnectionState.Initializing;

		try {
			// Check if room exists
			const roomExists = await this.signaling.roomExists(this.roomId);
			if (!roomExists) {
				throw new Error('Room not found');
			}

			this.state = ConnectionState.Connecting;
			
			// Set up event handlers for the signaling provider
			this.signaling.on('offerUpdate', (request) => this.handleOffer(request));
			
			// Create join request
			const joinRequest = await this.signaling.createJoinRequest(this.roomId);

			// Subscribe to updates to this join request
			const unsub = await this.signaling.subscribeToJoinRequestUpdates(joinRequest.id);
			this.cleanup.push(unsub);
		} catch (error) {
			this.state = ConnectionState.Disconnected;
			throw error;
		}
	}

	/**
	 * Handle a join request as the host
	 */
	private async handleJoinRequest(request: JoinRequest) {
		// Create a new peer for this client
		const peer = this.createPeer(true);  
		this.peers.set(request.requestee, peer);
		
		try {
			// Wait for the signal data
			const offer = await this.waitForSignalData(peer);
			
			// Update the join request with the offer
			await this.signaling.updateRequestWithOffer(request.id, offer);
		} catch (error) {
			// Clean up on error
			this.peers.delete(request.requestee);
			peer.destroy();
			await this.signaling.deleteJoinRequest(request.id);
			throw error;
		}
	}

	/**
	 * Handle an offer received as a client
	 */
	private async handleOffer(request: JoinRequest) {
		// Create a new peer for the host
		const hostPeer = this.createPeer(false);
		this.peers.set("host", hostPeer);
		
		// Signal the peer with the received offer
		hostPeer.signal(request.offer!);
		
		try {
			// Wait for the answer signal data
			const answer = await this.waitForSignalData(hostPeer);
			
			// Update the join request with the answer
			await this.signaling.updateRequestWithAnswer(request.id, answer);
			
			// Set up connection handler
			hostPeer.once('connect', () => {
				this.state = ConnectionState.Ready;
			});
		} catch (error) {
			// Clean up on error
			this.peers.delete("host");
			hostPeer.destroy();
			await this.signaling.deleteJoinRequest(request.id);
			throw error;
		}
	}

	/**
	 * Handle an answer received as the host
	 */
	private async handleAnswer(request: JoinRequest) {
		const peer = this.peers.get(request.requestee);
		if (!peer) {
			console.error('Peer not found');
			return;
		}
		
		// Complete the connection with the answer
		peer.signal(request.answer!);

		// Set up connection handler
		peer.once('connect', () => {
			this.signaling.deleteJoinRequest(request.id).catch(console.error);
		});
	}

	/**
	 * Create a new WebRTC peer
	 */
	private createPeer(isInitiator: boolean): SimplePeer.Instance {
		const peer = new SimplePeer({
			initiator: isInitiator,
			trickle: false,
		});
		
		peer.on('error', (error) => {
			Sentry.captureException(error);
			console.error('Peer error:', error);
			toast.error('Peer error: ' + error.message);
		});
		
		// Set up message handler
		peer.on('data', (data) => {
			try {
				const packet = JSON.parse(data.toString());
				this.emit('onMessage', packet);
			} catch (e) {
				console.error('Error parsing peer data:', e);
			}
		});
		
		return peer;
	}

	/**
	 * Wait for signal data from a peer
	 */
	private waitForSignalData(peer: SimplePeer.Instance): Promise<SimplePeer.SignalData> {
		return timeoutPromise(
			new Promise<SimplePeer.SignalData>((resolve) => {
				peer.once('signal', (data) => resolve(data));
			}),
			5000 // 5 second timeout
		);
	}
}